/**
 * Copyright (c) 2023 Spicy Friends Ltd.
 * All rights reserved.
 *
 * This source code is licensed under the proprietary license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { gql, type TypedDocumentNode } from '@apollo/client'
import type {
  AdminCreatePaymentRequestMutation,
  AdminCreatePaymentRequestMutationVariables,
  CreateFriendByProfileIdMutation,
  CreateFriendByProfileIdMutationVariables,
  CreatePaymentRequestMutation,
  CreatePaymentRequestMutationVariables,
  CreateReportMutation,
  CreateReportMutationVariables,
  DeleteCommentMutation,
  DeleteCommentMutationVariables,
  DeleteMediaMutation,
  DeleteMediaMutationVariables,
  ReadAllNotificationsMutation,
  ReadAllNotificationsMutationVariables,
  ReadNotificationsMutation,
  ReadNotificationsMutationVariables,
  RefundPaymentRequestMutation,
  RefundPaymentRequestMutationVariables,
  UpdateMediaMutation,
  UpdateMediaMutationVariables,
  UpdatePaymentApprovedMutation,
  UpdatePaymentApprovedMutationVariables,
  UpdatePaymentRequestMutation,
  UpdatePaymentRequestMutationVariables,
  UpdateProfileMutation,
  UpdateProfileMutationVariables,
  UpdateProfileNameMutation,
  UpdateProfileNameMutationVariables,
} from 'types/graphql'

/**
 * Notification Model
 */

export const READ_NOTIFICATIONS: TypedDocumentNode<ReadNotificationsMutation, ReadNotificationsMutationVariables> = gql`
  mutation ReadNotifications($input: NotificationListInput!) {
    readNotifications(input: $input) {
      count
    }
  }
`

export const READ_ALL_NOTIFICATIONS: TypedDocumentNode<
  ReadAllNotificationsMutation,
  ReadAllNotificationsMutationVariables
> = gql`
  mutation ReadAllNotifications {
    readAllNotifications {
      count
    }
  }
`

/**
 * Friend Model
 */

export const CREATE_FRIEND_BY_PROFILE_ID: TypedDocumentNode<
  CreateFriendByProfileIdMutation,
  CreateFriendByProfileIdMutationVariables
> = gql`
    mutation CreateFriendByProfileId($profileId: String!) {
      createFriendByProfileId(profileId: $profileId) {
        id
      }
    }
  `

/**
 * Comment Model
 */

export const DELETE_COMMENT: TypedDocumentNode<DeleteCommentMutation, DeleteCommentMutationVariables> = gql`
  mutation DeleteComment($commentId: String!) {
    deleteComment(commentId: $commentId) {
      id
      content
      markedDeleted
    }
  }
`

/**
 * Media Model
 */

export const UPDATE_MEDIA: TypedDocumentNode<UpdateMediaMutation, UpdateMediaMutationVariables> = gql`
  mutation UpdateMedia($id: String!, $input: UpdateMediaInput!) {
    updateMedia(id: $id, input: $input) {
      id
      caption
    }
  }
`

export const DELETE_MEDIA: TypedDocumentNode<DeleteMediaMutation, DeleteMediaMutationVariables> = gql`
  mutation DeleteMedia($id: String!) {
    deleteMedia(id: $id) {
      id
    }
  }
`

/**
 * Report Model
 */

export const CREATE_REPORT: TypedDocumentNode<CreateReportMutation, CreateReportMutationVariables> = gql`
  mutation CreateReport($input: CreateReportInput!) {
    createReport(input: $input) {
      publicReference
      caseId
    }
  }
`

/**
 * Profile Model
 */

export const UPDATE_PROFILE: TypedDocumentNode<UpdateProfileMutation, UpdateProfileMutationVariables> = gql`
  mutation UpdateProfile($id: String!, $input: UpdateProfileInput!) {
    updateProfile(id: $id, input: $input) {
      about
      coverMediaUrlLarge
      createdAt
      distance
      id
      isVerified
      verificationCount
      lastOnline
      lastVersion
      signUpStep
      signUpCompleted
      likeCount
      isLiked
      isFriend
      location
      locationLat
      locationLong
      maxAge
      minAge
      name
      profileMedia {
        fileName
        height
        id
        imageUrlLarge
        imageUrlMedium
        imageUrlSmall
        nsfwRating
        width
        ownerId
      }
      relationship
      supporter
      tagLine
      viewCount
    }
  }
`

export const UPDATE_PROFILE_NAME: TypedDocumentNode<UpdateProfileNameMutation, UpdateProfileNameMutationVariables> =
  gql`
  mutation UpdateProfileName($id: String!, $name: String!) {
    updateProfileName(id: $id, name: $name) {
      id
      name
    }
  }
`

/**
 * PaymentRequest (Supporter) Model
 */

export const CREATE_PAYMENT_REQUEST: TypedDocumentNode<
  CreatePaymentRequestMutation,
  CreatePaymentRequestMutationVariables
> = gql`
  mutation CreatePaymentRequest($input: CreatePaymentRequestInput!) {
    createPaymentRequest(input: $input) {
      id
      createdAt
      updatedAt
      profileId
      days
      partnerIds
      total
    }
  }
`

export const ADMIN_CREATE_PAYMENT_REQUEST: TypedDocumentNode<
  AdminCreatePaymentRequestMutation,
  AdminCreatePaymentRequestMutationVariables
> = gql`
  mutation AdminCreatePaymentRequest($profileId: String!, $input: CreatePaymentRequestInput!) {
    adminCreatePaymentRequest(profileId: $profileId, input: $input) {
      id
      createdAt
      updatedAt
      profileId
      days
      partnerIds
      total
    }
  }
`

export const UPDATE_PAYMENT_REQUEST: TypedDocumentNode<
  UpdatePaymentRequestMutation,
  UpdatePaymentRequestMutationVariables
> = gql`
  mutation UpdatePaymentRequest($id: String!, $input: UpdatePaymentRequestInput!) {
    updatePaymentRequest(id: $id, input: $input) {
      id
      createdAt
      updatedAt
      profileId
      days
      partnerIds
      total
    }
  }
`

export const REFUND_PAYMENT_REQUEST: TypedDocumentNode<
  RefundPaymentRequestMutation,
  RefundPaymentRequestMutationVariables
> = gql`
  mutation RefundPaymentRequest($id: String!) {
    refundPaymentRequest(id: $id) {
      id
      refunded
    }
  }
`

export const UPDATE_PAYMENT_APPROVED: TypedDocumentNode<
  UpdatePaymentApprovedMutation,
  UpdatePaymentApprovedMutationVariables
> = gql`
  mutation UpdatePaymentApproved($id: String!, $input: UpdatePaymentApprovedInput!) {
    updatePaymentApproved(id: $id, input: $input) {
      id
      createdAt
      updatedAt
      profileId
      days
      partnerIds
      total
      refunded
      notes
    }
  }
`
